<template>
  <div class="enrichment-success-banner">
    <div class="banner-content">
      <div class="banner-left">
        <div class="success-icon">
          <Icon icon="mdi:check" width="16" height="16" />
        </div>
        <div class="celebration-icon">
          <Icon icon="mdi:party-popper" width="18" height="18" />
        </div>
        <div class="banner-text">
          <h6 class="banner-title">Enrichment Complete!</h6>
          <div class="banner-details">
            <span class="detail-text">
              {{ contactsCount }} contact{{ contactsCount !== 1 ? "s" : "" }} added to
              <strong class="bucket-name">{{ bucketName || "contacts" }}</strong>
            </span>
            <span v-if="emailsCount > 0" class="detail-text detail-text--highlight"
              >{{ emailsCount }} email{{ emailsCount !== 1 ? "s" : "" }}</span
            >
            <span v-if="phonesCount > 0" class="detail-text detail-text--highlight"
              >{{ phonesCount }} phone{{ phonesCount !== 1 ? "s" : "" }}</span
            >
          </div>
        </div>
      </div>
      <div class="banner-right">
        <button class="btn-see-leads" @click="handleViewLeads">See the leads</button>
        <button class="btn-close" @click="handleClose" aria-label="Close">
          <Icon icon="mdi:close" width="16" height="16" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { Icon } from "@/components/ui";
import { CONTACTS_ROUTE, CONTACT_OVERVIEW_ROUTE } from "@/router/routes";

const ONE_HOUR_MS = 60 * 60 * 1000;

const props = defineProps({
  contactsCount: {
    type: Number,
    required: true,
  },
  emailsCount: {
    type: Number,
    required: true,
  },
  phonesCount: {
    type: Number,
    required: true,
  },
  bucketName: {
    type: String,
    default: null,
  },
  linkUrl: {
    type: String,
    default: null,
  },
  contactIds: {
    type: Array,
    default: () => [],
  },
  timestamp: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(["close", "view-leads"]);

const router = useRouter();
const route = useRoute();

const handleClose = () => {
  emit("close");
};

let autoDismissTimer = null;

const handleViewLeads = () => {
  emit("view-leads");

  if (props.contactIds.length === 1) {
    router.push({ name: CONTACT_OVERVIEW_ROUTE, params: { id: props.contactIds[0] } });
    return;
  }

  if (props.contactIds.length > 1) {
    router.push({
      name: CONTACTS_ROUTE,
      query: { contactIds: props.contactIds.join(",") },
    });
    return;
  }

  if (props.linkUrl) {
    const url = new URL(props.linkUrl, window.location.origin);
    const query = Object.fromEntries(url.searchParams);
    if (route.query.owner) query.owner = String(route.query.owner);
    router.push({ path: url.pathname, query });
    return;
  }

  const today = new Date();
  const startOfDay = new Date(today);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(today);
  endOfDay.setHours(23, 59, 59, 999);

  router.push({
    name: CONTACTS_ROUTE,
    query: {
      startDate: startOfDay.toISOString(),
      endDate: endOfDay.toISOString(),
    },
  });
};

onMounted(() => {
  const enrichmentTime = props.timestamp || Date.now();
  const elapsed = Date.now() - enrichmentTime;
  const remainingTime = ONE_HOUR_MS - elapsed;

  if (remainingTime <= 0) {
    handleClose();
    return;
  }

  autoDismissTimer = setTimeout(() => {
    handleClose();
  }, remainingTime);
});

onUnmounted(() => {
  if (autoDismissTimer) {
    clearTimeout(autoDismissTimer);
    autoDismissTimer = null;
  }
});
</script>

<style scoped>
.enrichment-success-banner {
  position: relative;
  width: 100%;
  background: #d1fae5;
  border-top: 1px solid #6ee7b7;
  border-bottom: 1px solid #6ee7b7;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 0;
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  max-width: 100%;
  margin: 0;
  gap: 12px;
}

.banner-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.success-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #059669;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(5, 150, 105, 0.3);
}

.celebration-icon {
  color: #f59e0b;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: bounce 1s ease-in-out infinite;
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

.banner-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.banner-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #065f46;
  line-height: 1.3;
}

.banner-details {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  font-size: 0.75rem;
  color: #374151;
  line-height: 1.4;
}

.detail-text {
  display: inline-flex;
  align-items: center;
}

.detail-text--highlight {
  color: #065f46;
  font-weight: 500;
}

.bucket-name {
  color: #374151;
  font-weight: 400;
  margin-left: 4px;
}

.detail-text:has-text {
  color: #065f46;
  font-weight: 500;
}

.banner-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.btn-see-leads {
  background-color: #065f46;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer !important;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-see-leads:hover {
  background-color: #047857;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(6, 95, 70, 0.3);
  cursor: pointer !important;
}

.btn-see-leads:active {
  transform: translateY(0);
}

.btn-close {
  background: transparent;
  border: none;
  color: #374151;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  opacity: 1;
}

.btn-close:hover {
  background-color: rgba(6, 78, 59, 0.1);
  opacity: 1;
}

@media (max-width: 768px) {
  .banner-content {
    flex-direction: column;
    align-items: flex-start;
    padding: 8px 12px;
  }

  .banner-right {
    width: 100%;
    justify-content: space-between;
  }

  .banner-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}
</style>
